import { useCallback, useEffect, useState } from "react";
import {
  AppConfigurationKey,
  BannerDto,
} from "../../../Common/api/models/AppConfiguration";
import {
  createAppConfig,
  deleteBannerConfig,
  getAppConfigForBanner,
  updateAppConfig,
} from "../../../Common/api/appConfigurationsHttpClient";
import { useSnackbar } from "notistack";

export const useAdminBannerConfig = () => {
  const [banners, setBanners] = useState<BannerDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editing, setEditing] = useState<BannerDto | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const initialBanner = {
    id: "",
    text: "",
    category: "info",
    variant: "filled",
    publishDate: "",
    unpublishDate: "",
  } as BannerDto;

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getAppConfigForBanner();
      setBanners(data);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (id: string) =>
    setEditing(banners.find((b) => b.id === id) || initialBanner);
  const cancelEdit = () => setEditing(null);

  const save = async (banner: BannerDto | Omit<BannerDto, "id">) => {
    setIsLoading(true);
    try {
      const appConfigValue = JSON.stringify({
        Text: banner.text,
        Category: banner.category,
        Variant: banner.variant,
        PublishDate: banner.publishDate,
        UnpublishDate: banner.unpublishDate,
        CustomBackgroundColor: banner.customBackgroundColor,
      });

      const id = (banner as BannerDto).id;
      let message = "";
      if (id) {
        await updateAppConfig({
          id: id,
          key: AppConfigurationKey.Banner,
          appConfigValue,
        });
        message = "The banner has been updated successfully.";
      } else {
        await createAppConfig({
          key: AppConfigurationKey.Banner,
          appConfigValue,
        });
        message = "The banner has been created successfully.";
      }

      enqueueSnackbar(message, {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });

      await load();
      window.dispatchEvent(new Event("bannerconfig:invalidate"));

      setEditing(null);
    } catch (ex) {
      enqueueSnackbar("Something went wrong while saving the banner.", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      console.error("Error saving banner: ", ex);
    } finally {
      setIsLoading(false);
    }
  };

  const remove = async (id: string) => {
    setIsLoading(true);
    try {
      await deleteBannerConfig(id);

      await load();
      window.dispatchEvent(new Event("bannerconfig:invalidate"));

      enqueueSnackbar("The banner has been deleted successfully.", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
    } catch (ex) {
      enqueueSnackbar("Something went wrong while deleting the banner.", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      console.error("Error deleting banner: ", ex);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    banners,
    isLoading,
    editing,
    startEdit,
    cancelEdit,
    save,
    remove,
  };
};
